import { useQuery, useQueryClient, useMutation, skipToken } from '@tanstack/react-query'
import useSession from '@hooks/useSession'
import {
  fetchContactsByParticipationId,
  updateContactForParticipation,
  deleteContactForParticipation,
} from '@/services/contribution/api'
import { DEFAULT_CONTACT_GOAL } from '@/util/defaults'

export interface ContactData {
  id?: string
  name: string
  alias: string
  email: string
  phone: string
  language?: 'en' | 'es'
}

export default function useContacts() {
  const queryClient = useQueryClient()

  const { session } = useSession()

  const { data, isLoading, error } = useQuery({
    queryKey: ['contacts'],
    queryFn: session ? () => fetchContactsByParticipationId(session.participationId) : skipToken,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: !!session,
  })

  /**
 * We're using the inline mutations to create and bulk create contacts right now. We will
 * refactor to consolidate them here at a later time.
  const createContactMutation = useMutation({
    mutationFn: (contactData: ContactData) =>
      createContactForParticipation({
        ...contactData,
        participationId: session.participationId,
      }),
  })

  async function createContact({ contact }: { contact: ContactData }) {
    if (!session) {
      throw new Error('No session available')
    }

    const newContact = await createContactMutation.mutateAsync(contact)
    queryClient.setQueryData(['contacts'], (oldData: Contact[]) => {
      return [...oldData, newContact]
    })

    return newContact
  }
  */

  const updateContactMutation = useMutation({
    mutationFn: (contactData: ContactData) =>
      updateContactForParticipation({
        contactId: contactData.id?.toString(),
        name: contactData.name.trim(),
        alias: contactData.alias.trim(),
        email: contactData.email.trim(),
        phone: contactData.phone.trim(),
      }),
  })

  async function updateContact({ contact }: { contact: ContactData }) {
    if (!contact?.id) {
      throw new Error('Contact ID is required to update.')
    }

    const updatedContact = await updateContactMutation.mutateAsync(contact)

    queryClient.invalidateQueries({ queryKey: ['contacts'] })
    return updatedContact
  }

  const deleteContactMutation = useMutation({
    mutationFn: (contactData: { contactId: string }) => deleteContactForParticipation(contactData),
  })

  async function deleteContact(contactData: { contactId: string }) {
    if (!contactData.contactId) {
      throw new Error('Contact ID is required to delete.')
    }

    const success = await deleteContactMutation.mutateAsync(contactData)

    queryClient.invalidateQueries({ queryKey: ['contacts'] })
    return success
  }

  return {
    contacts: data?.contacts || [],
    contactGoal: data?.contactGoal || DEFAULT_CONTACT_GOAL,
    goals: data?.goals,
    isLoading,
    error,
    // createContact,
    updateContact,
    deleteContact,
  }
}
